import { resolveUpiAppDisplay } from './mf-execution/upiAppConfig'

export const INSTALLED_UPI_INTENTS_UPDATED = 'installedUpiIntentsUpdated'

export type InstalledUpiApp = {
  name: string
  packageName: string
  appLogoUrl: string
}

export type InstalledUpiIntentsUpdatedEvent = CustomEvent<InstalledUpiApp[]>

export function convertToJSON(rawString: string): Record<string, unknown> | null {
  const trimmed = rawString?.trim() ?? ''
  if (!trimmed) return null

  try {
    const formattedJsonString = trimmed
      .replace(/([{,[]\s*)([a-zA-Z0-9_]+):/g, '$1"$2":')
      .replace(/:\s*([^,{}\[\]]+)(?=[,}\]])/g, (_match, value) => `: "${String(value).trim()}"`)
    return JSON.parse(formattedJsonString) as Record<string, unknown>
  } catch {
    return null
  }
}

export function getInstalledUpiAppsFromStorage(): InstalledUpiApp[] {
  return parseInstalledUpiApps(localStorage.getItem('installedUpiApps') ?? '')
}

/** Parses Flutter/localStorage payload; normalizes alternate native key names per app entry. */
export function parseInstalledUpiApps(raw: string): InstalledUpiApp[] {
  const trimmed = raw?.trim() ?? ''
  if (!trimmed) return []

  let payload: Record<string, unknown> | null = null
  try {
    // Dev mocks use JSON.stringify; Flutter may send looser format via convertToJSON.
    payload = JSON.parse(trimmed) as Record<string, unknown>
  } catch {
    payload = convertToJSON(trimmed)
  }

  const upiApps = payload?.upiApps
  if (!Array.isArray(upiApps)) return []

  return upiApps.map((item) => normalizeInstalledUpiApp(item)).filter((app): app is InstalledUpiApp => app !== null)
}

export function notifyInstalledUpiIntentsFromFlutter(raw: string): InstalledUpiApp[] {
  const apps = parseInstalledUpiApps(raw)
  window.dispatchEvent(new CustomEvent<InstalledUpiApp[]>(INSTALLED_UPI_INTENTS_UPDATED, { detail: apps }))
  return apps
}

/**
 * Maps native/Flutter UPI app objects to a stable shape. Accepts `package` / `package_name` etc.
 * so `upiPackageName` on orders is always a package id, not a display name.
 */
export function normalizeInstalledUpiApp(raw: unknown): InstalledUpiApp | null {
  if (!raw || typeof raw !== 'object') return null

  const app = raw as Record<string, unknown>
  const name = readString(app?.name) ?? readString(app?.appName) ?? readString(app?.displayName) ?? ''
  const packageName =
    readString(app?.packageName) ??
    readString(app?.package) ??
    readString(app?.package_name) ??
    readString(app?.appPackageName) ??
    ''
  const appLogoUrl = readString(app?.appLogoUrl) ?? readString(app?.logoUrl) ?? readString(app?.iconUrl) ?? ''

  if (!name && !packageName) return null

  const resolved = resolveUpiAppDisplay(packageName, { name, logoUrl: appLogoUrl })

  return {
    name: resolved.name || packageName,
    packageName,
    appLogoUrl: resolved.logoUrl
  }
}

export function readString(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed || null
}
